import { useMutation } from "@tanstack/react-query";
import { original } from "immer";
import { toast } from "react-toastify";

import { useSelectedWorkspaceContext } from "common/context/SelectedWorkspaceContext";
import useUpdateLead from "common/datahooks/useUpdateLead";
import { del, patch, post } from "common/helpers/HTTP";
import type { Lead, Note } from "common/types";

import useUpdateDetailedLead from "./useUpdateDetailedLead";

async function addNote(
  workspaceId: string,
  profileId: string,
  text: string,
  abortSignal: AbortSignal,
) {
  const { note: newNote } = await post(
    `workspaces/${workspaceId}/leads/${profileId}/notes`,
    {
      text: text.trim(),
    },
    abortSignal,
  );
  return { profileId, note: newNote };
}
async function updateNote(
  workspaceId: string,
  profileId: string,
  noteId: string,
  text: string,
) {
  await patch(`workspaces/${workspaceId}/leads/${profileId}/notes/${noteId}`, {
    text,
  });
  return { profileId, noteId, text: text.trim() };
}
async function deleteNote(
  workspaceId: string,
  profileId: string,
  noteId: string,
) {
  await del(`workspaces/${workspaceId}/leads/${profileId}/notes/${noteId}`);
  return { profileId, noteId };
}

export default function useLeadNotes() {
  const { id: workspaceId } = useSelectedWorkspaceContext();

  const { updateDetailedLead } = useUpdateDetailedLead();
  const { updateLead } = useUpdateLead();

  async function updateCachedLead(
    leadId: string,
    updateFunction: (lead: Lead) => void,
  ) {
    await updateLead(leadId, updateFunction);
    await updateDetailedLead(leadId, updateFunction);
  }

  const { mutateAsync: addLeadNote, isPending: isAddingLeadNote } = useMutation(
    {
      mutationFn: ({
        text,
        profileId,
        abortSignal,
      }: {
        text: string;
        profileId: string;
        abortSignal?: AbortSignal;
      }) => addNote(workspaceId, profileId, text, abortSignal),
      onSuccess: async ({
        profileId,
        note,
      }: {
        profileId: string;
        note: Note;
      }) => {
        await updateCachedLead(profileId, (lead) => {
          lead.notes.unshift(note);
        });
      },
    },
  );
  const { mutateAsync: updateLeadNote, isPending: isUpdatingLeadNote } =
    useMutation({
      mutationFn: ({
        profileId,
        newText,
        noteId,
      }: {
        profileId: string;
        newText: string;
        noteId: string;
      }) => updateNote(workspaceId, profileId, noteId, newText),
      onMutate: async ({ profileId, newText, noteId }) => {
        let originalText: string;
        let originalEditedAt: number;

        await updateCachedLead(profileId, (lead) => {
          for (let j = 0; j < lead.notes.length; j += 1) {
            if (lead.notes[j].id === noteId) {
              const { text, edited_at: editedAt } = original(lead.notes[j]);
              originalText = text;
              originalEditedAt = editedAt;

              lead.notes[j].text = newText;
              lead.notes[j].edited_at = Date.now();
              return;
            }
          }
        });

        return { originalText, originalEditedAt };
      },
      onError: async (
        _,
        { profileId, noteId },
        { originalText, originalEditedAt },
      ) => {
        await updateCachedLead(profileId, (lead) => {
          for (let j = 0; j < lead.notes.length; j += 1) {
            if (lead.notes[j].id === noteId) {
              lead.notes[j].text = originalText;
              lead.notes[j].edited_at = originalEditedAt;
              return;
            }
          }
        });

        toast.error("Error updating Note");
      },
    });
  const { mutateAsync: deleteLeadNote, isPending: isDeletingLeadNote } =
    useMutation({
      mutationFn: ({
        profileId,
        noteId,
      }: {
        profileId: string;
        noteId: string;
      }) => deleteNote(workspaceId, profileId, noteId),
      onMutate: async ({ profileId, noteId }) => {
        let previousNotes: Note[];

        await updateCachedLead(profileId, (lead) => {
          previousNotes = original(lead.notes);
          lead.notes = lead.notes.filter(({ id }) => id !== noteId);
        });

        return { previousNotes };
      },
      onError: async (_, { profileId }, { previousNotes }) => {
        await updateCachedLead(profileId, (lead) => {
          lead.notes = previousNotes;
        });

        toast.error("Error deleting Note");
      },
    });
  return {
    addLeadNote,
    isAddingLeadNote,
    updateLeadNote,
    isUpdatingLeadNote,
    deleteLeadNote,
    isDeletingLeadNote,
  };
}
